import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plane, Clock, MapPin, CheckCircle2, AlertCircle, RotateCw, Trash2, Filter, ChevronRight, Search } from 'lucide-react';
import { toast } from 'sonner';
import { listFlights, removeFlight } from '../../lib/flightSyncDb';
import { flushQueue } from '../../lib/flightSyncApi';

const SYNC_FILTERS = ['TODOS', 'PENDENTES', 'SINCRONIZADOS'];

export default function FlightsLocalPage({ ctx }) {
  const navigate = useNavigate();
  const [flights, setFlights] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filterDate, setFilterDate] = useState(''); // YYYY-MM-DD
  const [filterDrone, setFilterDrone] = useState('');
  const [filterSync, setFilterSync] = useState('TODOS');

  const refresh = useCallback(async () => {
    const items = await listFlights();
    setFlights(items);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const droneOptions = useMemo(() => {
    const set = new Set(flights.map((f) => f.drone_model).filter(Boolean));
    return ['', ...set];
  }, [flights]);

  const filtered = useMemo(() => {
    return flights.filter((f) => {
      if (filterSync === 'PENDENTES' && f.synced) return false;
      if (filterSync === 'SINCRONIZADOS' && !f.synced) return false;
      if (filterDrone && f.drone_model !== filterDrone) return false;
      if (filterDate) {
        const day = (f.start_time || f.created_at || '').slice(0, 10);
        if (day !== filterDate) return false;
      }
      return true;
    });
  }, [flights, filterSync, filterDrone, filterDate]);

  const handleSync = async () => {
    if (!navigator.onLine) return toast.error('Sem conexao');
    setBusy(true);
    const r = await flushQueue(ctx.token);
    setBusy(false);
    if (r.ran) toast.success(`Sync: ${r.ok} ok · ${r.fail} erro(s)`);
    refresh();
  };

  const handleDelete = async (id, e) => {
    e?.stopPropagation();
    await removeFlight(id);
    toast.success('Removido');
    refresh();
  };

  const pendingCount = filtered.filter((f) => !f.synced).length;
  const syncedCount = filtered.filter((f) => f.synced).length;
  const filtersActive = filterDate || filterDrone || filterSync !== 'TODOS';

  return (
    <div data-testid="fs-flights-page">
      <div className="fs-hero">
        <div className="fs-hero-label">MEUS VOOS</div>
        <div className="fs-hero-value">{filtered.length}</div>
        <div className="fs-hero-sub">
          {pendingCount} aguardando · {syncedCount} sincronizados
          {filtersActive && <span style={{ color: 'var(--fs-cyan)', marginLeft: 6 }}>· filtro ativo</span>}
        </div>
      </div>

      <div className="fs-row" style={{ marginBottom: 10 }}>
        <button
          className="fs-btn fs-btn-ghost"
          onClick={() => setShowFilters((v) => !v)}
          style={{ padding: '10px' }}
          data-testid="fs-toggle-filters"
        >
          <Filter size={14} /> FILTROS {filtersActive && <span style={{ color: 'var(--fs-cyan)' }}>●</span>}
        </button>
        {pendingCount > 0 && (
          <button
            className="fs-btn fs-btn-primary"
            onClick={handleSync}
            disabled={busy}
            style={{ flex: 2 }}
            data-testid="fs-btn-sync-now"
          >
            <RotateCw size={14} className={busy ? 'fs-spin' : ''} />
            {busy ? 'SINCRONIZANDO...' : `SYNC ${pendingCount}`}
          </button>
        )}
      </div>

      {showFilters && (
        <div className="fs-card" data-testid="fs-filters-panel">
          <div className="fs-card-title"><Search size={12} /> FILTRAR</div>
          <label className="fs-label">Data</label>
          <input
            type="date"
            className="fs-input"
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
            data-testid="fs-filter-date"
          />
          <div style={{ height: 10 }} />
          <label className="fs-label">Drone</label>
          <select
            className="fs-input"
            value={filterDrone}
            onChange={(e) => setFilterDrone(e.target.value)}
            data-testid="fs-filter-drone"
          >
            <option value="">Todos os drones</option>
            {droneOptions.filter(Boolean).map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <div style={{ height: 10 }} />
          <label className="fs-label">Status de sync</label>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {SYNC_FILTERS.map((opt) => (
              <button
                key={opt}
                className={`fs-btn ${filterSync === opt ? 'fs-btn-primary' : 'fs-btn-ghost'}`}
                style={{ padding: '8px 12px', fontSize: 11, flex: 1, minWidth: 90 }}
                onClick={() => setFilterSync(opt)}
                data-testid={`fs-filter-sync-${opt.toLowerCase()}`}
              >
                {opt}
              </button>
            ))}
          </div>
          {filtersActive && (
            <button
              onClick={() => { setFilterDate(''); setFilterDrone(''); setFilterSync('TODOS'); }}
              className="fs-btn fs-btn-ghost"
              style={{ width: '100%', marginTop: 12, fontSize: 11 }}
              data-testid="fs-filter-clear"
            >
              LIMPAR FILTROS
            </button>
          )}
        </div>
      )}

      <div className="fs-card">
        {filtered.length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--fs-text-dim)', textAlign: 'center', padding: 20 }}>
            {flights.length === 0 ? 'Nenhum voo importado ainda' : 'Nenhum voo corresponde aos filtros'}
          </p>
        ) : (
          filtered.map((f) => (
            <div
              key={f.id}
              className={`fs-flight-item ${f.synced ? 'synced' : 'pending'}`}
              onClick={() => navigate(`/flightsync/flights/${f.id}`)}
              style={{ cursor: 'pointer' }}
              data-testid={`fs-flight-item-${f.id}`}
            >
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <Plane size={14} style={{ color: 'var(--fs-cyan)' }} />
                  <span style={{ fontWeight: 600, fontSize: 14 }}>{f.drone_model || 'DJI'}</span>
                  {f.synced
                    ? <span className="fs-pill fs-pill-green">✓ SYNCED</span>
                    : <span className="fs-pill fs-pill-amber">PENDENTE</span>}
                </div>
                <div className="fs-flight-meta">
                  <Clock size={10} style={{ verticalAlign: 'middle', marginRight: 3 }} />
                  {secToMin(f.duration_seconds)} ·
                  <MapPin size={10} style={{ verticalAlign: 'middle', margin: '0 3px' }} />
                  {(f.distance_meters / 1000).toFixed(2)} km ·
                  {(f.max_altitude_meters || 0).toFixed(0)} m alt
                </div>
                <div className="fs-flight-meta" style={{ marginTop: 2 }}>
                  {f.start_time ? new Date(f.start_time).toLocaleString('pt-BR') : '-'}
                </div>
                {f.last_error && (
                  <div style={{ fontSize: 10, color: 'var(--fs-red)', marginTop: 3 }}>ERRO: {f.last_error}</div>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <button
                  onClick={(e) => handleDelete(f.id, e)}
                  title="Remover"
                  style={{ background: 'transparent', border: 'none', color: 'var(--fs-text-dim)', cursor: 'pointer', padding: 6 }}
                  data-testid={`fs-delete-flight-${f.id}`}
                >
                  <Trash2 size={14} />
                </button>
                <ChevronRight size={14} style={{ color: 'var(--fs-cyan)' }} />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function secToMin(s) {
  if (!s) return '0:00';
  const m = Math.floor(s / 60);
  const ss = Math.floor(s % 60).toString().padStart(2, '0');
  return `${m}:${ss}`;
}
